import { IMAGEKIT_BASE_URL } from "../config/imagekit";

/**
 * Obtiene la URL optimizada a través de ImageKit.
 * Soporta transformaciones al vuelo como ancho y calidad.
 */
export const getOptimizedImageUrl = (
  imagePath: string,
  width?: number,
  quality: number = 80
): string => {
  if (!imagePath) return "";

  // Si ya es una URL absoluta externa (ej: Supabase directa, HTTP, etc.) o base64, no la procesamos con la base
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://") || imagePath.startsWith("data:")) {
    return imagePath;
  }

  // Obtenemos la URL base de ImageKit
  const baseUrl = IMAGEKIT_BASE_URL || "";

  if (!baseUrl) {
    return imagePath;
  }

  // Asegurar formato correcto de barras: base sin '/' al final y path con '/' al inicio
  const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  let cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
  
  // Dado que las imágenes se subieron conservando la estructura de la carpeta 'public'
  // en tu panel de ImageKit, añadimos el prefijo /public de forma automática.
  if (!cleanPath.startsWith("/public/")) {
    cleanPath = `/public${cleanPath}`;
  }
  
  const fullUrl = `${cleanBase}${cleanPath}`;

  // Agregar parámetros de transformación de ImageKit (?tr=w-X,q-Y)
  const transforms: string[] = [];
  if (width) {
    transforms.push(`w-${width}`);
  }
  if (quality) {
    transforms.push(`q-${quality}`);
  }

  const finalUrl = transforms.length > 0
    ? `${fullUrl}?tr=${transforms.join(",")}`
    : fullUrl;

  // Codificar la URL final usando encodeURI para que los espacios se conviertan en %20.
  // Esto es CRÍTICO para evitar que el navegador rompa la URL al parsear el atributo 'srcSet' (responsivo).
  return encodeURI(finalUrl);
};

/**
 * Genera el atributo srcSet responsivo utilizando ImageKit para entregar
 * el tamaño de imagen más óptimo según el dispositivo del cliente.
 */
export const generateImageSrcSet = (imagePath: string): string => {
  if (!imagePath) return "";

  // Si no está configurada la variable, regresamos el fallback local sin transformaciones
  const baseUrl = IMAGEKIT_BASE_URL;

  if (!baseUrl) {
    return `
      ${imagePath} 400w,
      ${imagePath} 800w,
      ${imagePath} 1200w
    `.trim();
  }

  return `
    ${getOptimizedImageUrl(imagePath, 400)} 400w,
    ${getOptimizedImageUrl(imagePath, 800)} 800w,
    ${getOptimizedImageUrl(imagePath, 1200)} 1200w
  `.trim();
};

